import styled from '@emotion/styled';
import {css} from '@emotion/react';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';

/* The remedy Modal body (ModalContent) is itself max-height:60vh / overflow:auto. To avoid a
   second scrollbar, this container is sized to a definite height just under that, lays out as a
   flex column, and makes the Thread the only overflow region (flex:1 + min-height:0). The modal
   body then has nothing left to scroll. */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 55vh;
`;

/* Compact, dismissable disclaimer bar -- smaller than a full Banner so it does not dominate the
   modal, and can be closed to read the whole conversation unobstructed. */
export const Disclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.xSmall};
  padding: ${sizes.xSmall} ${sizes.small};
  background: ${colors.blue1};
  border-radius: 6px;
  font-size: 1.2rem;
  line-height: 1.4;
  color: ${colors.charcoal};
`;

export const DisclaimerText = styled.div`
  flex: 1;
`;

export const DisclaimerDismiss = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.gray4};
  line-height: 1;

  &:hover {
    color: ${colors.charcoal};
  }
`;

/* Sync status row shown under the disclaimer while a sent message is reconciling. Lives in the
   fixed top region (not the composer) so showing it never resizes the compose box. */
export const SyncStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.xSmall};
  padding: ${sizes.xSmall} ${sizes.small};
  font-size: 1.2rem;
  color: ${colors.gray4};
`;

export const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.small};
  padding: ${sizes.small} ${sizes.medium} ${sizes.small} 5px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const EmptyThread = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20rem;
  color: ${colors.gray4};
`;

export const Bubble = styled.div((props: { own: boolean }) => css`
  display: flex;
  flex-direction: column;
  gap: ${sizes.xxSmall};
  align-self: ${props.own ? 'flex-end' : 'flex-start'};
  max-width: 70%;
`);

export const BubbleSender = styled.div((props: { own: boolean }) => css`
  font-size: 1.2rem;
  color: ${colors.gray4};
  text-align: ${props.own ? 'right' : 'left'};
`);

export const BubbleContent = styled.div((props: { own: boolean }) => css`
  padding: ${sizes.xSmall} ${sizes.small};
  border-radius: 12px;
  background: ${props.own ? colors.primary1 : colors.gray1};
  color: ${props.own ? colors.white : colors.charcoal};
  white-space: pre-wrap;
  word-break: break-word;
`);

export const BubbleTime = styled.div((props: { own: boolean }) => css`
  font-size: 1.1rem;
  color: ${colors.gray4};
  text-align: ${props.own ? 'right' : 'left'};
`);

export const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.xSmall};
  border-top: 1px solid ${colors.gray2};
  padding-top: ${sizes.small};
`;

export const ComposerRow = styled.div`
  display: flex;
  gap: ${sizes.small};
  align-items: stretch;
`;

export const ComposerActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.xSmall};
  flex-shrink: 0;
`;

export const ComposerInput = styled.textarea`
  flex: 1;
  resize: vertical;
  min-height: 8rem;
  max-height: 16rem;
  padding: ${sizes.xSmall} ${sizes.small};
  border: 1px solid ${colors.gray3};
  border-radius: 6px;
  font-family: inherit;
  font-size: 1.4rem;

  &:disabled {
    background: ${colors.gray1};
    color: ${colors.gray4};
  }
`;

export const CharCount = styled.div`
  align-self: flex-start;
  font-size: 1.1rem;
  color: ${colors.gray4};
`;
